import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useEffect, useRef, useState } from 'react';
import { storage } from '../../lib/firebase';
import { isOwnedBy } from '../auth';
import type { PostMinimal } from './usePosts';

/**
 * 「自分の写真を 1 枚ずつ個別ダウンロード」用のフック + 保存ヘルパー (2026-06-13)。
 *
 * 地区設定 `photoConfig.photoDownloadMode === 'individual'` のとき PhotoDownloadModal が使う。
 *
 * **重要 (2026-06-13 修正)**: 当初はクライアントから `where('authorUid','in',...)` で Firestore を
 * 直接クエリしていたが、posts の read ルールは visibility ベース (public / 本人 hidden_others のみ) で、
 * visibility 無制約の list クエリはルールが許可できず `Missing or insufficient permissions` で拒否される。
 * そこで **アプリが既に読み込み済みの posts (ルール準拠)** を受け取り、自分の写真だけ抽出する方式に変更した。
 * ZIP (サーバ Admin SDK) と異なり admin が hidden_all 化した自分の投稿は含まないが、それらは元々
 * 参加者には見えないため実用上の差はない。
 *
 * 対象は「自分」= 現 uid ∪ 端末引き継ぎ元 (useMyIdentity allUids、R-70)。保存は写真ごとに
 * `savePhotoToDevice` (blob fetch → `<a download>`)。1 タップ=1 ダウンロードなので iOS/Android とも確実。
 */

export interface MyPhotoItem {
  postId: string;
  /** 保存対象 (原寸 original.jpg の download URL)。 */
  downloadUrl: string;
  /** 一覧プレビュー用 (サムネ、無ければ原寸)。 */
  thumbUrl: string;
  /** 保存ファイル名 (`YYYYMMDD-HHmmss_NNN.jpg`)。 */
  filename: string;
  title: string;
  /** docs/28 §5-4: ぼかし前の写真 (元画像) を保存するか (false = ぼかし済み)。 */
  original: boolean;
}

export type MyPhotosState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; photos: MyPhotoItem[] }
  | { status: 'error'; error: Error };

function isHttp(v: unknown): v is string {
  return typeof v === 'string' && v.startsWith('http');
}

/** PostMinimal.createdAt (Timestamp | null) → epoch ミリ秒 (不明は 0)。 */
function toMillis(ts: PostMinimal['createdAt']): number {
  if (ts && typeof ts.toMillis === 'function') {
    try {
      return ts.toMillis();
    } catch {
      return 0;
    }
  }
  return 0;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** epoch ミリ秒 → `YYYYMMDD-HHmmss` (ローカル時刻、ファイル名の sort 用)。0 は 'nodate'。 */
function stamp(ms: number): string {
  if (!ms) return 'nodate';
  const d = new Date(ms);
  return (
    `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}` +
    `-${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`
  );
}

/**
 * 読み込み済み posts から「自分の写真」一覧を導出するフック。
 * `enabled` が true (個別モードで modal open) のときのみ計算する。
 */
export function useMyPhotoDownloads(
  posts: readonly PostMinimal[],
  allUids: string[],
  enabled: boolean,
): { state: MyPhotosState; reload: () => void } {
  const [state, setState] = useState<MyPhotosState>({ status: 'idle' });
  const [tick, setTick] = useState(0);

  // posts は呼出側で新参照になり得る (例: AnalysisPage の未 ready 時の [])。effect 依存に含めると
  // 再計算ループになるため ref 経由で最新を読む (再計算は enabled / allUids / tick の変化で行う)。
  const postsRef = useRef(posts);
  useEffect(() => {
    postsRef.current = posts;
  }, [posts]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      setState({ status: 'loading' });
      try {
        const mine = postsRef.current
          .filter((p) => !!p.photoPath && isOwnedBy(p.authorUid, { allUids }))
          .slice()
          .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt)); // 新しい順
        const items: MyPhotoItem[] = [];
        let idx = 1;
        for (const p of mine) {
          // docs/28 §5-4 (D7): ぼかし前の写真 (元画像) があればそれを渡す (本人だけが読める)。
          let original = false;
          let url: string | null = null;
          if (p.photoOriginalPath) {
            try {
              url = await getDownloadURL(storageRef(storage, p.photoOriginalPath));
              original = true;
            } catch {
              url = null;
            }
          }
          // 原寸 URL: doc 保存済 photoUrl を優先、無ければ Storage path から getDownloadURL で解決。
          if (!url) url = isHttp(p.photoUrl) ? p.photoUrl : null;
          if (!url && p.photoPath) {
            try {
              url = await getDownloadURL(storageRef(storage, p.photoPath));
            } catch {
              url = null;
            }
          }
          if (!url) {
            idx += 1;
            continue;
          }
          const thumb = isHttp(p.photoThumbUrl) ? p.photoThumbUrl : url;
          items.push({
            postId: p.id,
            downloadUrl: url,
            thumbUrl: thumb,
            filename: `${stamp(toMillis(p.createdAt))}_${String(idx).padStart(3, '0')}.jpg`,
            title: p.title ?? '',
            original,
          });
          idx += 1;
        }
        if (!cancelled) setState({ status: 'ready', photos: items });
      } catch (e) {
        if (!cancelled) setState({ status: 'error', error: e as Error });
      }
    })();
    return () => {
      cancelled = true;
    };
    // posts は postsRef 経由で読むため依存に含めない (新参照ループ回避)。
  }, [enabled, allUids, tick]);

  return { state, reload: () => setTick((t) => t + 1) };
}

/**
 * 1 枚の写真を端末に保存する (blob fetch → `<a download>`)。
 * Storage は CORS GET 許可済 (storage.cors.json)。1 ユーザー操作 = 1 ダウンロードなので iOS Safari でも動く。
 */
export async function savePhotoToDevice(url: string, filename: string): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`ダウンロードに失敗しました (${res.status})`);
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
  }
}
